/**
 * RecentReportsTab Component
 * Displays list of submitted power reports with date filtering
 * Mobile cards / Desktop table with search, pagination, and PDF viewer
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  DocumentTextIcon,
  NoSymbolIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { getReportPdf, voidAchPayment } from '../../../../services/oathPowerReportService';
import { OathButton, OathModal, OathEmptyState, OathAlert } from '../../../shared/OathUI';
import ReportCard from './ReportCard';
import { SortableHeader, Pagination, MobileLoadingSkeleton, TableLoadingSkeleton } from './shared';
import PdfViewerModal from '../modals/PdfViewerModal';
import { formatDate, formatCurrencyWithSymbol, blobToBase64, getTodayForInput, getDateDaysAgoForInput, getDateMonthsAgoForInput } from '../utils/formatters';
import { PAGINATION_OPTIONS, SEARCH_DEBOUNCE_MS, VOID_ACH } from '../utils/constants';
import { useIsMobile, usePdfCache } from '../utils/hooks';

const RecentReportsTab = ({
  reports = [],
  isLoading = false,
  onRefresh,
  pagination = {},
  onPaginationChange,
  searchTerm = '',
  onSearchChange,
  dateRange = { fromDate: '', toDate: '' },
  onDateRangeChange,
  includeSubAgencies = true,
  onIncludeSubAgenciesChange
}) => {
  const isMobile = useIsMobile();
  const { getCachedPdf, setCachedPdf } = usePdfCache();
  const [sortField, setSortField] = useState('reportDate');
  const [sortDirection, setSortDirection] = useState('desc');

  // PDF viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [pdfReportNumber, setPdfReportNumber] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);

  // Void ACH state
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [reportToVoid, setReportToVoid] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidLoading, setVoidLoading] = useState(false);

  // Timer tick state — increments every 60s to re-evaluate NACHA countdown
  const [, setTimerTick] = useState(0);

  // Local search state
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const debounceTimerRef = useRef(null);

  // Sync local state when prop changes
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Debounced search handler
  const handleSearchInputChange = useCallback((value) => {
    setLocalSearchTerm(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (onSearchChange) {
        onSearchChange(value);
      }
    }, SEARCH_DEBOUNCE_MS);
  }, [onSearchChange]);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setLocalSearchTerm('');
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (onSearchChange) {
      onSearchChange('');
    }
  }, [onSearchChange]);

  // Local date range state - holds invalid (from > to) combos without fetching
  const [localDateRange, setLocalDateRange] = useState(dateRange);

  // Whether the custom From/To inputs are expanded
  const [customRangeOpen, setCustomRangeOpen] = useState(false);

  // Sync local state when prop changes (e.g. agency change resets the range)
  useEffect(() => {
    setLocalDateRange(dateRange);
  }, [dateRange]);

  // Quick range presets. 'All time' omits fromDate = unbounded lookback,
  // back to the agency's first online submission.
  const datePresets = useMemo(() => {
    const today = getTodayForInput();
    return [
      { key: '30d', label: '30 days', range: { fromDate: getDateDaysAgoForInput(30), toDate: today } },
      { key: '3m', label: '3 months', range: { fromDate: getDateMonthsAgoForInput(3), toDate: today } },
      { key: '6m', label: '6 months', range: { fromDate: getDateMonthsAgoForInput(6), toDate: today } },
      { key: '1y', label: '1 year', range: { fromDate: getDateMonthsAgoForInput(12), toDate: today } },
      { key: 'all', label: 'All time', range: { fromDate: '', toDate: today } }
    ];
  }, []);

  const activePresetKey = customRangeOpen
    ? 'custom'
    : (datePresets.find(p => p.range.fromDate === localDateRange.fromDate && p.range.toDate === localDateRange.toDate)?.key ?? 'custom');

  const isDateRangeInvalid = Boolean(
    localDateRange.fromDate && localDateRange.toDate && localDateRange.fromDate > localDateRange.toDate
  );

  const handlePresetClick = useCallback((preset) => {
    setCustomRangeOpen(false);
    setLocalDateRange(preset.range);
    onDateRangeChange?.(preset.range);
  }, [onDateRangeChange]);

  // Date inputs emit '' or a complete YYYY-MM-DD - no debounce needed.
  // YYYY-MM-DD strings compare correctly lexicographically.
  const handleDateChange = useCallback((field, value) => {
    setLocalDateRange(prev => {
      const next = { ...prev, [field]: value };
      if (!(next.fromDate && next.toDate && next.fromDate > next.toDate)) {
        onDateRangeChange?.(next);
      }
      return next;
    });
  }, [onDateRangeChange]);

  // Cleanup debounce + tick every 60s for NACHA countdown
  useEffect(() => {
    const interval = setInterval(() => setTimerTick(t => t + 1), 60000);
    return () => {
      clearInterval(interval);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Handle view report PDF (with caching)
  const handleViewReport = async (report) => {
    setPdfReportNumber(report.reportNumber);
    setPdfViewerOpen(true);

    // Check cache first
    const cachedPdf = getCachedPdf(report.powerReportId);
    if (cachedPdf) {
      setPdfData(cachedPdf);
      return;
    }

    setPdfLoading(true);

    try {
      const result = await getReportPdf(report.powerReportId);
      if (result.success && result.data) {
        // Convert Blob to base64 string for PdfViewerModal
        const base64Data = await blobToBase64(result.data);
        setPdfData(base64Data);
        // Cache the PDF for future views
        setCachedPdf(report.powerReportId, base64Data);
      } else {
        toast.error('Failed to load PDF: ' + (result.error || 'Unknown error'));
        setPdfViewerOpen(false);
      }
    } catch (err) {
      toast.error('Failed to load PDF');
      setPdfViewerOpen(false);
    } finally {
      setPdfLoading(false);
    }
  };

  // Handle void ACH - triggered from ReportCard via onVoid prop
  const handleVoidClick = (report) => {
    setReportToVoid(report);
    setVoidReason('');
    setVoidConfirmOpen(true);
  };

  // Check if report can be voided (ACH only, before NACHA file is created, within 24h)
  const canVoidReport = (report) => {
    return VOID_ACH.canVoid(report.electronicPayment, report.isAchProcessed, report.dateSubmitted || report.reportDate);
  };

  // Get void time remaining for a report (countdown to NACHA cutoff)
  const getVoidTimeRemaining = (report) => {
    return VOID_ACH.getRemainingTime(report.nachaCutoffUtc);
  };

  const confirmVoid = async () => {
    if (!reportToVoid) return;

    setVoidLoading(true);

    try {
      const result = await voidAchPayment(reportToVoid.powerReportId, voidReason);
      if (result.success) {
        toast.success(`ACH payment voided for ${reportToVoid.reportNumber}`);
        onRefresh();
      } else {
        toast.error(result.error || 'Failed to void payment');
      }
    } catch (err) {
      toast.error('Failed to void payment');
      console.error(err);
    } finally {
      setVoidLoading(false);
      setVoidConfirmOpen(false);
      setReportToVoid(null);
      setVoidReason('');
    }
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    onPaginationChange?.({ page: newPage, pageSize: pagination.pageSize });
  };

  const handlePageSizeChange = (newPageSize) => {
    onPaginationChange?.({ page: 0, pageSize: newPageSize });
  };

  // Sort reports locally - memoized to avoid re-sorting on every render
  const sortedReports = useMemo(() => {
    return [...reports].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'reportDate' || sortField === 'dateCreated') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [reports, sortField, sortDirection]);

  return (
    <>
      {/* Search + Date Range Filter Bar */}
      <div className="mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative flex-1 max-w-xs min-w-[200px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by report number..."
              value={localSearchTerm}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-500"
            />
            {localSearchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full transition-colors"
              >
                <XMarkIcon className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
          <div>
            <span className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              Date range
            </span>
            <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Date range">
              {datePresets.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => handlePresetClick(preset)}
                  aria-pressed={activePresetKey === preset.key}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                    activePresetKey === preset.key
                      ? 'bg-primary border-primary text-white shadow-sm'
                      : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCustomRangeOpen(true)}
                aria-pressed={activePresetKey === 'custom'}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  activePresetKey === 'custom'
                    ? 'bg-primary border-primary text-white shadow-sm'
                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                Custom
              </button>
            </div>
          </div>
          <label className="flex items-center gap-2 py-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeSubAgencies}
              onChange={(e) => onIncludeSubAgenciesChange?.(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
            />
            Include sub-agencies
          </label>
        </div>
        {(customRangeOpen || activePresetKey === 'custom') && (
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div>
              <label htmlFor="recent-from-date" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                From Date
              </label>
              <input
                id="recent-from-date"
                type="date"
                value={localDateRange.fromDate}
                max={getTodayForInput()}
                onChange={(e) => handleDateChange('fromDate', e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
              />
            </div>
            <div>
              <label htmlFor="recent-to-date" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                To Date
              </label>
              <input
                id="recent-to-date"
                type="date"
                value={localDateRange.toDate}
                max={getTodayForInput()}
                onChange={(e) => handleDateChange('toDate', e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
              />
            </div>
            <p className="pb-2 text-xs text-gray-500 dark:text-gray-400">
              Leave From Date empty to include all history
            </p>
          </div>
        )}
        {isDateRangeInvalid && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">
            From date must be on or before To date
          </p>
        )}
        {searchTerm && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Showing results for "{searchTerm}"
          </p>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        isMobile ? <MobileLoadingSkeleton /> : <TableLoadingSkeleton />
      ) : reports.length === 0 ? (
        <OathEmptyState
          icon={DocumentTextIcon}
          title={(searchTerm || dateRange.fromDate || dateRange.toDate) ? 'No reports found' : 'No recent reports'}
          message={searchTerm
            ? `No reports found matching "${searchTerm}".`
            : (dateRange.fromDate || dateRange.toDate)
              ? 'No reports found in the selected date range.'
              : 'Submitted reports will appear here.'
          }
        />
      ) : isMobile ? (
        /* Mobile Card View */
        <div className="space-y-3">
          {sortedReports.map((report) => (
            <ReportCard
              key={report.powerReportId}
              report={report}
              onView={handleViewReport}
              onVoid={handleVoidClick}
              showDelete={false}
              showPaymentMethod={true}
              showNetBuf={false}
              showVoid={true}
              canVoid={canVoidReport(report)}
              voidTimeRemaining={canVoidReport(report) ? getVoidTimeRemaining(report) : null}
            />
          ))}
        </div>
      ) : (
        /* Desktop Table View */
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <SortableHeader label="Report #" field="reportNumber" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Batch #" field="batchId" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Date" field="reportDate" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="# Bonds" field="powerCount" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} align="center" />
                <SortableHeader label="Liability" field="totalBondAmount" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} align="right" />
                {/* NET/BUF intentionally omitted — those figures live on the report PDF */}
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Payment</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {sortedReports.map((report) => (
                  <tr
                    key={report.powerReportId}
                    onClick={() => handleViewReport(report)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      {report.reportNumber}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {report.batchId || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(report.reportDate)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-center">
                      {report.powerCount || 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                      {formatCurrencyWithSymbol(report.totalBondAmount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        report.electronicPayment
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {report.electronicPayment ? 'ACH' : 'Check'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      {canVoidReport(report) ? (() => {
                        const remaining = getVoidTimeRemaining(report);
                        const tooltip = remaining
                          ? `Void ACH payment (${remaining.formatted} until NACHA cutoff)`
                          : 'Void ACH payment';
                        return (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleVoidClick(report); }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                            title={tooltip}
                          >
                            <NoSymbolIcon className="h-4 w-4" />
                            Void
                          </button>
                        );
                      })() : (
                        <span className="text-gray-400 dark:text-gray-500 text-xs">-</span>
                      )}
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {reports.length > 0 && (
        <Pagination
          page={pagination.page || 0}
          pageSize={pagination.pageSize || PAGINATION_OPTIONS.PAGE_SIZE}
          totalCount={pagination.totalCount || 0}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      {/* PDF Viewer Modal */}
      <PdfViewerModal
        isOpen={pdfViewerOpen}
        onClose={() => { setPdfViewerOpen(false); setPdfData(null); }}
        pdfData={pdfData}
        reportNumber={pdfReportNumber}
        isLoading={pdfLoading}
      />

      {/* Void ACH Confirmation Modal */}
      <OathModal
        isOpen={voidConfirmOpen}
        onClose={() => setVoidConfirmOpen(false)}
        title="Void ACH Payment"
        size="md"
        footer={
          <div className="flex gap-3 justify-end w-full">
            <OathButton variant="outline" onClick={() => setVoidConfirmOpen(false)} disabled={voidLoading}>
              Cancel
            </OathButton>
            <OathButton
              variant="danger"
              onClick={confirmVoid}
              loading={voidLoading}
            >
              Void Payment
            </OathButton>
          </div>
        }
      >
        {/* Warning Alert */}
        <OathAlert variant="warning" className="mb-4">
          <div>
            <strong>Warning:</strong> This action cannot be undone. Voiding this payment will:
            <ul className="mt-2 ml-5 list-disc text-sm">
              <li>Permanently remove the report and all associated payment records</li>
              <li>Cancel all ACH payment transactions</li>
              <li>Send notifications to Agent, PowerDesk, and Regional Team</li>
            </ul>
          </div>
        </OathAlert>

        {/* Report Details */}
        {reportToVoid && (() => {
          const remaining = getVoidTimeRemaining(reportToVoid);
          return (
          <div className="mb-4 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Report Details:</p>
            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <p><span className="font-medium">Report #:</span> {reportToVoid.reportNumber}</p>
              <p><span className="font-medium">Date Submitted:</span> {formatDate(reportToVoid.reportDate)}</p>
              <p><span className="font-medium">Total NET:</span> {formatCurrencyWithSymbol(reportToVoid.totalNet)}</p>
              <p><span className="font-medium">Total BUF:</span> {formatCurrencyWithSymbol(reportToVoid.totalBuf)}</p>
              {remaining && (
                <p className="mt-2 text-orange-600 dark:text-orange-400 font-medium">
                  Time remaining to void: {remaining.formatted}
                </p>
              )}
            </div>
          </div>
          );
        })()}

        {/* Reason Input */}
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Please enter a reason for voiding this payment (optional):
          </p>
          <textarea
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            placeholder="e.g., Incorrect payment amount, duplicate submission, etc."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary"
            rows={2}
          />
        </div>
      </OathModal>
    </>
  );
};

export default RecentReportsTab;
